<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--titlegroup">
        <span class="a-header--title slide-down-enter-active">Change Events Settings</span>
        <HelpButton :content="HELP_CONTENT.settings" />
      </span>
    </div>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <div v-if="!isLoading" class="a-body flex-grow-1 overflow-y-auto p-20-px">
      <!-- The form is only shown once the saved settings are known. Filling it
           with the recommended defaults after a failed load would look right
           and let Save overwrite the account's real settings. -->
      <a-alert
        v-if="loadFailed"
        type="error"
        show-icon
        message="The current settings could not be loaded."
      >
        <template slot="description">
          <p class="mb-2">Nothing can be saved until they are loaded.</p>
          <a-button type="primary" size="small" :loading="isRetrying" @click="retryLoad">Try again</a-button>
        </template>
      </a-alert>

      <a-alert
        v-else
        class="mb-3"
        type="info"
        show-icon
        message="A change applies from the next cycle, within one minute."
        description="It is not retroactive. Groups that already exist stay as they are."
      />

      <a-row v-if="!loadFailed" :gutter="24">
        <a-col :span="24">
          <a-form :form="form" @submit="onSubmit">
            <!-- Two tabs, ONE form and ONE Save.
                 forceRender keeps the fields of the hidden tab mounted. Without
                 it a-tabs destroys them, the form loses their values, and Save
                 writes an empty setting from a tab the person never opened. -->
            <a-tabs v-model="tab" class="ce-tabs">
              <a-tab-pane key="incidents" tab="Site-Wide Incidents" forceRender>
            <div class="ce-section">
              <p class="ce-cardtitle">Notification Mode</p>
              <a-form-item :extra="notifyModeHelp">
                <a-radio-group v-decorator="['notify_mode', { rules: [{ required: true }] }]">
                  <a-radio
                    v-for="mode in NOTIFY_MODES"
                    :key="mode.value"
                    class="ce-mode"
                    :value="mode.value"
                    :disabled="isModeDisabled(mode)"
                  >
                    <span class="ce-mode__head">
                      <span class="ce-mode__label">{{ mode.label }}</span>
                      <span v-if="mode.value === savedMode" class="ce-mode__tag ce-mode__tag--now">Current</span>
                      <span v-if="mode.holdsAlertsBack" class="ce-mode__tag ce-mode__tag--warn">Holds alerts back</span>
                    </span>
                    <span class="ce-mode__detail">{{ mode.detail }}</span>
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </div>

            <div class="ce-section">
              <p class="ce-cardtitle">Opening Thresholds</p>
              <a-form-item label="Window opening criteria (HIGH or MEDIUM alerts)" :extra="floodThresholdHelp">
                <a-input-number
                  :min="LIMITS.flood_threshold.min"
                  :max="LIMITS.flood_threshold.max"
                  :step="1"
                  :precision="0"
                  class="full-w"
                  v-decorator="[
                    'flood_threshold',
                    {
                      rules: [
                        { required: true, message: 'Enter a number of alerts' },
                        { validator: validateWholeNumber('flood_threshold', 'Alerts before a site-wide incident opens') }
                      ]
                    }
                  ]"
                />
              </a-form-item>
              <a-form-item label="Alerts received within (minutes)" :extra="floodWindowHelp">
                <a-input-number
                  :min="LIMITS.flood_window_minutes.min"
                  :max="LIMITS.flood_window_minutes.max"
                  :step="5"
                  :precision="0"
                  class="full-w"
                  v-decorator="[
                    'flood_window_minutes',
                    {
                      rules: [
                        { required: true, message: 'Enter a window in minutes' },
                        { validator: validateWholeNumber('flood_window_minutes', 'Counting window') }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </div>

            <div class="ce-section">
              <p class="ce-cardtitle">Automatic Closure</p>
              <a-form-item label="Auto close window after inactivity (minutes)" :extra="autoResolveHelp">
                <a-input-number
                  :min="LIMITS.auto_resolve_minutes.min"
                  :max="LIMITS.auto_resolve_minutes.max"
                  :step="30"
                  :precision="0"
                  class="full-w"
                  v-decorator="[
                    'auto_resolve_minutes',
                    {
                      rules: [
                        { required: true, message: 'Enter a quiet period in minutes' },
                        { validator: validateWholeNumber('auto_resolve_minutes', 'Quiet minutes before a site-wide incident closes') }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </div>

            <div class="ce-section">
              <p class="ce-cardtitle">Summary Intervals</p>
              <a-form-item label="Minutes between summary messages" :extra="backoffHelp">
                <a-input
                  placeholder="60, 240, 720, 1440"
                  v-decorator="[
                    'backoff_minutes',
                    {
                      rules: [
                        { required: true, message: 'Enter one to ten numbers, separated by commas' },
                        { validator: validateBackoff }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </div>
              </a-tab-pane>

              <a-tab-pane key="grouping" tab="Grouped Alerts" forceRender>
            <div class="ce-section">
              <p class="ce-cardtitle">Automatic Closure</p>
              <a-form-item label="Group closes after inactivity (minutes)" :extra="groupWindowHelp">
                <a-input-number
                  :min="LIMITS.group_window_minutes.min"
                  :max="LIMITS.group_window_minutes.max"
                  :step="15"
                  :precision="0"
                  class="full-w"
                  v-decorator="[
                    'group_window_minutes',
                    {
                      rules: [
                        { required: true, message: 'Enter a quiet period in minutes' },
                        { validator: validateWholeNumber('group_window_minutes', 'Quiet minutes before an aggregated alert closes') }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </div>

            <div class="ce-section">
              <p class="ce-cardtitle">Grouping Rules</p>
              <a-form-item label="Minimum alert similarity (0.05 to 0.95)" :extra="similarityHelp">
                <a-input-number
                  :min="LIMITS.similarity_threshold.min"
                  :max="LIMITS.similarity_threshold.max"
                  :step="LIMITS.similarity_threshold.step"
                  class="full-w"
                  v-decorator="[
                    'similarity_threshold',
                    {
                      rules: [
                        { required: true, message: 'Enter a value between 0.05 and 0.95' },
                        { validator: validateSimilarity }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </div>

              </a-tab-pane>
            </a-tabs>

            <div class="d-flex align-items-center">
              <a-button type="primary" html-type="submit" :loading="isSaving" :disabled="loadFailed">Save</a-button>
              <a-button class="ml-2" :disabled="isSaving" @click="resetToLoaded">Undo Changes</a-button>
              <a-button class="ml-2" type="link" :disabled="isSaving" @click="resetToDefaults">Restore Defaults</a-button>
            </div>
          </a-form>
        </a-col>
      </a-row>
    </div>
  </div>
</template>

<script>
  import ChangeEventsService from '../services/ChangeEvents.service';
  import HelpButton from './HelpButton.vue';
  import { CONFIG_LIMITS, NOTIFY_MODES, NOTIFY_MODE_DEFAULT, floodRuleText } from '../consts/ChangeEvents.consts';
  import { HELP_CONTENT } from '../consts/helpContent';

  export default {
    name: 'ChangeEventSettings',
    components: { HelpButton },
    data() {
      return {
        /** Which tab is open. Both tabs write into the same form. */
        tab: 'incidents',
        NOTIFY_MODES,
        // Whether the database can store a mode at all. Before sql/007 runs
        // there is no column, and the old boolean cannot tell 'all' from
        // 'log_only', so that choice is disabled rather than saved as
        // something else.
        notifyModeSupported: false,
        HELP_CONTENT,
        LIMITS: CONFIG_LIMITS,
        form: this.$form.createForm(this, { name: 'change_events_config' }),
        isLoading: true,
        isSaving: false,
        // True when GET /change_events/config failed. The form stays hidden and
        // Save stays off, so the defaults can never be written over the
        // account's real settings by mistake.
        loadFailed: false,
        isRetrying: false,
        // What the API last gave us, so "Undo my changes" can put it back.
        loadedConfig: null
      };
    },
    computed: {
      /** The mode as it is stored, so one option can be marked Current. */
      savedMode() {
        const saved = (this.loadedConfig || {}).notify_mode;
        return NOTIFY_MODES.some((m) => m.value === saved) ? saved : NOTIFY_MODE_DEFAULT;
      },
      notifyModeHelp() {
        return 'Only Summaries Only holds an alert back. The other two options do not change the alert emails a customer receives today.';
      },
      floodThresholdHelp() {
        const l = CONFIG_LIMITS.flood_threshold;
        // The same sentence the banner above the incident list shows, so the
        // two screens an administrator compares say one thing.
        return `${floodRuleText('this many')} inside the counting window opens a site-wide incident. ${l.min} to ${l.max}. Recommended: ${l.default}.`;
      },
      floodWindowHelp() {
        const l = CONFIG_LIMITS.flood_window_minutes;
        return `The rolling window that count uses. ${l.min} to ${l.max} minutes. Recommended: ${l.default}.`;
      },
      groupWindowHelp() {
        const l = CONFIG_LIMITS.group_window_minutes;
        return `An aggregated alert closes after this much quiet. ${l.min} to ${l.max} minutes. Recommended: ${l.default}.`;
      },
      autoResolveHelp() {
        const l = CONFIG_LIMITS.auto_resolve_minutes;
        return `A site-wide incident closes itself after this much quiet. ${l.min} to ${l.max} minutes. Recommended: ${l.default}.`;
      },
      similarityHelp() {
        const l = CONFIG_LIMITS.similarity_threshold;
        return `A higher number groups fewer alerts together. ${l.min} to ${l.max}. Recommended: ${l.default}.`;
      },
      backoffHelp() {
        const l = CONFIG_LIMITS.backoff_minutes;
        return `One to ten numbers, separated by commas, each larger than the one before. Each between ${l.min} and ${l.max} minutes. The last one repeats. Recommended: ${l.default.join(', ')}.`;
      }
    },
    mounted() {
      this.fetchConfig();
    },
    methods: {
      /**
       * Whether a choice can be made.
       *
       * All Alerts and Summaries needs a column that may not exist yet. The
       * older schema cannot tell it from Record Only, so offering it would save
       * one thing and show another.
       */
      isModeDisabled(mode) {
        return mode.value === 'all' && !this.notifyModeSupported;
      },
      /**
       * Turns an API config object into the values the form holds.
       * `backoff_minutes` is a list on the wire and a comma list in the field.
       * @param {object} config
       * @returns {object}
       */
      toFormValues(config) {
        const source = config || {};
        const backoff = Array.isArray(source.backoff_minutes)
          ? source.backoff_minutes
          : String(source.backoff_minutes || '')
              .split(',')
              .map((v) => v.trim())
              .filter(Boolean);
        return {
          flood_threshold: this.numberOrDefault(source.flood_threshold, CONFIG_LIMITS.flood_threshold.default),
          flood_window_minutes: this.numberOrDefault(source.flood_window_minutes, CONFIG_LIMITS.flood_window_minutes.default),
          group_window_minutes: this.numberOrDefault(source.group_window_minutes, CONFIG_LIMITS.group_window_minutes.default),
          auto_resolve_minutes: this.numberOrDefault(source.auto_resolve_minutes, CONFIG_LIMITS.auto_resolve_minutes.default),
          similarity_threshold: this.numberOrDefault(source.similarity_threshold, CONFIG_LIMITS.similarity_threshold.default),
          backoff_minutes: (backoff.length ? backoff : CONFIG_LIMITS.backoff_minutes.default).join(', '),
          // A mode the screen does not know about, or none at all, shows as the
          // one that changes nothing.
          notify_mode: NOTIFY_MODES.some((m) => m.value === source.notify_mode)
            ? source.notify_mode
            : NOTIFY_MODE_DEFAULT
        };
      },
      numberOrDefault(value, fallback) {
        const num = Number(value);
        return Number.isFinite(num) ? num : fallback;
      },
      /**
       * Builds a validator for a whole number inside its allowed range.
       * @param {string} field key in CONFIG_LIMITS
       * @param {string} label what the message calls it
       * @returns {(rule: object, value: *, callback: Function) => void}
       */
      validateWholeNumber(field, label) {
        const limit = CONFIG_LIMITS[field];
        return (rule, value, callback) => {
          const num = Number(value);
          if (!Number.isFinite(num) || !Number.isInteger(num)) {
            callback(`${label} must be a whole number.`);
            return;
          }
          if (num < limit.min || num > limit.max) {
            callback(`${label} must be between ${limit.min} and ${limit.max}.`);
            return;
          }
          callback();
        };
      },
      validateSimilarity(rule, value, callback) {
        const limit = CONFIG_LIMITS.similarity_threshold;
        const num = Number(value);
        if (!Number.isFinite(num) || num < limit.min || num > limit.max) {
          callback(`This must be between ${limit.min} and ${limit.max}.`);
          return;
        }
        callback();
      },
      /**
       * Summary Intervals: one to ten whole numbers, each inside the
       * range, and each larger than the one before it.
       */
      validateBackoff(rule, value, callback) {
        const limit = CONFIG_LIMITS.backoff_minutes;
        const parts = String(value || '')
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v !== '');
        if (parts.length < limit.minCount || parts.length > limit.maxCount) {
          callback(`Enter between ${limit.minCount} and ${limit.maxCount} numbers, separated by commas.`);
          return;
        }
        let previous = null;
        for (const part of parts) {
          const num = Number(part);
          if (!Number.isFinite(num) || !Number.isInteger(num)) {
            callback('Each value must be a whole number of minutes.');
            return;
          }
          if (num < limit.min || num > limit.max) {
            callback(`Each value must be between ${limit.min} and ${limit.max} minutes.`);
            return;
          }
          if (previous !== null && num <= previous) {
            callback('Each value must be larger than the one before it.');
            return;
          }
          previous = num;
        }
        callback();
      },
      async fetchConfig() {
        try {
          const config = await ChangeEventsService.fetchConfig();
          this.loadedConfig = config || {};
          this.notifyModeSupported = this.loadedConfig.notify_mode_supported !== false;
          this.loadFailed = false;
        } catch (err) {
          this.loadFailed = true;
          this.$showMultiErrorMessage(err.messages);
        } finally {
          // The form is behind v-if="!isLoading". Its fields do not exist
          // until the spinner goes, so the values must be set AFTER that, on
          // the next tick, or they land on nothing and the page opens empty.
          // Only a successful load fills the form: toFormValues(null) would be
          // the recommended defaults, not this account's settings.
          const fill = () => this.$nextTick(() => {
            if (!this.loadFailed) this.form.setFieldsValue(this.toFormValues(this.loadedConfig));
          });
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
              fill();
            }, 300);
          } else {
            fill();
          }
        }
      },
      async retryLoad() {
        if (this.isRetrying) return;
        this.isRetrying = true;
        try {
          await this.fetchConfig();
        } finally {
          this.isRetrying = false;
        }
      },
      resetToLoaded() {
        this.form.setFieldsValue(this.toFormValues(this.loadedConfig));
      },
      resetToDefaults() {
        this.form.setFieldsValue(this.toFormValues({}));
      },
      onSubmit(e) {
        e.preventDefault();
        if (this.loadFailed) return;
        this.form.validateFields((err, values) => {
          if (err) return;
          // Switching to a mode that holds alerts back stops emails the
          // customer receives today, on every website. That is asked about
          // once, the way closing a window is; a change of numbers is not.
          const target = NOTIFY_MODES.find((m) => m.value === values.notify_mode);
          if (target && target.holdsAlertsBack && values.notify_mode !== this.savedMode) {
            this.$confirm({
              title: 'Switch to Summaries Only?',
              content:
                'While a window is open, individual alerts on that website are no longer emailed. One summary is sent instead. This applies to every website on this account from the next cycle.',
              okText: 'Switch and save',
              cancelText: 'Keep the current mode',
              onOk: () => this.saveValues(values)
            });
            return;
          }
          this.saveValues(values);
        });
      },
      async saveValues(values) {
        try {
          this.isSaving = true;
          const payload = {
            flood_threshold: Number(values.flood_threshold),
            flood_window_minutes: Number(values.flood_window_minutes),
            group_window_minutes: Number(values.group_window_minutes),
            auto_resolve_minutes: Number(values.auto_resolve_minutes),
            similarity_threshold: Number(values.similarity_threshold),
            notify_mode: values.notify_mode,
            // A list of numbers on the wire. The API stores the string form.
            backoff_minutes: String(values.backoff_minutes)
              .split(',')
              .map((v) => Number(v.trim()))
              .filter((v) => Number.isFinite(v))
          };
          const saved = await ChangeEventsService.updateConfig(payload);
          this.loadedConfig = saved && Object.keys(saved).length ? saved : payload;
          this.form.setFieldsValue(this.toFormValues(this.loadedConfig));
          this.$showSuccessMessage('Settings saved. They apply from the next cycle.');
        } catch (error) {
          this.$showMultiErrorMessage(error.messages);
        } finally {
          this.isSaving = false;
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  // The radio sits beside the label, on the same line, and the description
  // wraps under it. It used to be display:block on the whole option, which put
  // the radio above the text.
  .ce-mode {
    display: flex;
    align-items: flex-start;
    margin: 0 0 16px;
    white-space: normal;
    height: auto;
    line-height: 1.5;

    // ant-design puts the circle in the first span and the content in the
    // second. The content has to grow, or the description will not wrap.
    ::v-deep > span:last-child {
      flex: 1;
      padding-left: 8px;
      white-space: normal;
    }

    &__head {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
    }
    &__label {
      font-weight: 600;
      color: #262626;
    }
    &__tag {
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      padding: 1px 6px;
      border-radius: 3px;
      white-space: nowrap;

      &--now {
        color: #17808f;
        background: #e6f7f9;
      }
      &--off {
        color: #8c8c8c;
        background: #f0f0f0;
      }
      // The only choice that stops an email reaching a customer says so, where
      // the choice is made.
      &--warn {
        color: #a8390f;
        background: #fdece4;
      }
    }
    &__detail {
      display: block;
      margin-top: 2px;
      color: #595959;
    }
  }
  .ce-cardtitle {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #8a94a0;
    margin: 0 0 10px;
  }
  .ce-section {
    background: #ffffff;
    border: 1px solid #e3e7ea;
    border-radius: 8px;
    padding: 16px 18px;
    margin-bottom: 16px;
  }
  .ce-sidenote {
    background: #ffffff;
    border: 1px solid #e3e7ea;
    border-radius: 8px;
    padding: 16px 18px;
    font-size: 12.5px;
    color: #5b6570;
  }
</style>
